import { MaintenanceApi } from '@/openapi/apis/MaintenanceApi';
import { createConfiguration } from './api-client';

let maintenanceCheckInProgress = false;

/**
 * Redirects to maintenance page with current URL as redirect parameter
 */
const redirectToMaintenancePage = () => {
  window.location.href = '/maintenance.html?redirect=' + encodeURIComponent(window.location.href);
};

/**
 * Checks if maintenance is enabled by calling the maintenance API
 * @returns {Promise<boolean>} True if maintenance is enabled
 */
const checkMaintenanceStatus = async () => {
  try {
    // Avoid multiple concurrent maintenance checks
    if (maintenanceCheckInProgress) {
      return false;
    }

    maintenanceCheckInProgress = true;

    // Create a simple API instance without auth interceptor to avoid infinite loops
    const maintenanceApi = new MaintenanceApi(createConfiguration());
    const response = await maintenanceApi.getMaintenance();

    return response?.isOn || false;
  } catch (error) {
    console.error('Failed to check maintenance status:', error);
    return false;
  } finally {
    maintenanceCheckInProgress = false;
  }
};

/**
 * Checks maintenance status at app startup and redirects if needed
 * @returns {Promise<boolean>} True if maintenance is enabled and user was redirected
 */
export const checkMaintenanceOnStartup = async () => {
  try {
    const isMaintenanceEnabled = await checkMaintenanceStatus();

    if (isMaintenanceEnabled) {
      // Redirect to static maintenance page
      redirectToMaintenancePage();
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking maintenance on startup:', error);
    return false;
  }
};

/**
 * Handles 503 Service Unavailable responses by checking maintenance status
 * and redirecting to maintenance.html if maintenance is enabled
 * @param {Response} response - The fetch response object
 * @returns {Promise<Response>} The original response or throws if maintenance redirect occurs
 */
export const handleMaintenanceResponse = async (response) => {
  if (response.status === 503) {
    const isMaintenanceEnabled = await checkMaintenanceStatus();

    if (isMaintenanceEnabled) {
      // Redirect to static maintenance page
      redirectToMaintenancePage();
      // This will stop execution, but we throw an error just in case
      throw new Error('Redirecting to maintenance page');
    }
  }

  return response;
};

/**
 * Middleware for OpenAPI runtime that handles 503 maintenance responses
 */
export const createMaintenanceMiddleware = () => ({
  post: async (context) => {
    const { response } = context;
    if (response) {
      await handleMaintenanceResponse(response);
    }
    return response;
  }
}); 